import { z } from "zod";
import {
  answerQuestionIdSchema,
  questionIdSchema,
  REPEATED_FIELD_ADDRESS_PREFIX,
} from "./ids";
import { questionsForAnswers } from "./groups";
import { fieldQuestion, repeatFieldsOf } from "./repeatable";
import type { AnswerMap, AnswerValue, FormContent, Question } from "./types";

const addressSchema = z.tuple([
  questionIdSchema,
  questionIdSchema,
  questionIdSchema,
]);

/** JSON tuple framing is unambiguous even for imported IDs containing punctuation. */
export function repeatedFieldAddress(
  questionId: string,
  entryId: string,
  fieldId: string,
): string {
  return `${REPEATED_FIELD_ADDRESS_PREFIX}${JSON.stringify([questionId, entryId, fieldId])}`;
}

export function parseRepeatedFieldAddress(
  id: string,
): [string, string, string] | null {
  if (!id.startsWith(REPEATED_FIELD_ADDRESS_PREFIX)) {
    return null;
  }

  try {
    const parsed = addressSchema.safeParse(
      JSON.parse(id.slice(REPEATED_FIELD_ADDRESS_PREFIX.length)),
    );
    return parsed.success &&
      `${REPEATED_FIELD_ADDRESS_PREFIX}${JSON.stringify(parsed.data)}` === id
      ? parsed.data
      : null;
  } catch {
    return null;
  }
}

export const uploadQuestionIdSchema = z
  .string()
  .max(2000)
  .refine(
    (id) =>
      answerQuestionIdSchema.safeParse(id).success ||
      !!parseRepeatedFieldAddress(id),
    "Invalid question identity.",
  );

/** A real pinned question wins over a subsequently introduced address syntax. */
export function resolveRepeatedFieldAddress(
  form: FormContent,
  id: string,
): [string, string, string] | null {
  return questionsForAnswers(form).some((question) => question.id === id)
    ? null
    : parseRepeatedFieldAddress(id);
}

export function resolveAnswerQuestion(
  form: FormContent,
  id: string,
): Question | undefined {
  const questions = questionsForAnswers(form);
  const direct = questions.find((q) => q.id === id);

  if (direct) {
    return direct;
  }

  const address = parseRepeatedFieldAddress(id);

  if (!address) {
    return;
  }

  const [parentId, , fieldId] = address;
  const parent = questions.find(
    (q) => q.id === parentId && q.type === "repeatable_group",
  );
  const field = parent && repeatFieldsOf(parent).find((f) => f.id === fieldId);
  return field && parent
    ? { ...fieldQuestion(field), id, sectionId: parent.sectionId }
    : undefined;
}

/** File rows are server-owned. Never trust a file embedded in a client-written group answer. */
export function assembleQuestionAnswer(
  question: Question,
  value: AnswerValue | undefined,
  answers: AnswerMap,
): AnswerValue | undefined {
  if (value?.kind !== "entries") {
    return value;
  }

  const files = repeatFieldsOf(question).filter(
    (field) => field.type === "file_upload",
  );

  if (!files.length) {
    return value;
  }

  return {
    kind: "entries",
    entries: value.entries.map((entry) => {
      const values = { ...entry.values };

      for (const field of files) {
        delete values[field.id];
        const file =
          answers[repeatedFieldAddress(question.id, entry.id, field.id)];

        if (file?.kind === "files") {
          values[field.id] = file;
        }
      }

      return { ...entry, values };
    }),
  };
}

export function containsFileAnswer(
  value: AnswerValue | undefined | null,
): boolean {
  return (
    value?.kind === "files" ||
    (value?.kind === "entries" &&
      value.entries.some((entry) =>
        Object.values(entry.values).some(
          (field) => typeof field !== "string" && field.kind === "files",
        ),
      ))
  );
}

export function withoutEmbeddedFiles(
  value: AnswerValue | undefined,
): AnswerValue | undefined {
  if (value?.kind !== "entries" || !containsFileAnswer(value)) {
    return value;
  }

  return {
    kind: "entries",
    entries: value.entries.map((entry) => ({
      ...entry,
      values: Object.fromEntries(
        Object.entries(entry.values).filter(
          ([, field]) => typeof field === "string" || field.kind !== "files",
        ),
      ),
    })),
  };
}

/** Preserve independently owned file rows when freezing a response. */
export function embeddedFileRows(
  questionId: string,
  value: AnswerValue,
): AnswerMap {
  if (value.kind !== "entries") {
    return {};
  }

  return Object.fromEntries(
    value.entries.flatMap((entry) =>
      Object.entries(entry.values).flatMap(([fieldId, field]) =>
        typeof field !== "string" && field.kind === "files"
          ? [[repeatedFieldAddress(questionId, entry.id, fieldId), field]]
          : [],
      ),
    ),
  );
}

/** Files offered by an assembled visible answer, including its repeated entries. */
export function fileRefsInAnswer(
  value: AnswerValue | undefined,
): import("./types").FileRef[] {
  if (value?.kind === "files") {
    return value.files;
  }

  if (value?.kind !== "entries") {
    return [];
  }

  return value.entries.flatMap((entry) =>
    Object.values(entry.values).flatMap((field) =>
      typeof field !== "string" && field.kind === "files" ? field.files : [],
    ),
  );
}

/** Drafts retain hidden entries, but removed entries cannot own stored files. */
export function withoutOrphanedRepeatedFiles(
  answers: AnswerMap,
  form: FormContent,
): AnswerMap {
  return Object.fromEntries(
    Object.entries(answers).filter(([id]) => {
      const address = resolveRepeatedFieldAddress(form, id);

      if (!address) {
        return true;
      }

      const parent = answers[address[0]];
      return (
        parent?.kind === "entries" &&
        parent.entries.some((entry) => entry.id === address[1])
      );
    }),
  );
}
